import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { DrawerLink } from '../../app/DrawerLink';
import { Link } from 'react-router-dom';

import { Explicit } from '../../components/ui/Explicit';
import { Card, Chip } from '../../components/ui/Card';
import { ReceivedReviews } from './ReceivedReviews';
import { TrackEditor } from './TrackEditor';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { useArtistTracks } from './useArtistTracks';
import { albums } from '../albums/api';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { AddToPlaylist } from '../library/AddToPlaylist';
import { SkeletonRow } from '../../components/ui/Loader';
import { copy } from '../../config/copy';
import { api } from '../../lib/api';
import { usePlayer, type PlayableTrack } from '../../stores/player';

export interface TrackSummary extends PlayableTrack {
  slug: string;
  /**
   * The artist's own words. `TrackRepository::shape()` has always returned it;
   * this interface simply never said so, which is how the dashboard's hero came
   * to render a field TypeScript did not know existed.
   */
  description: string | null;
  favorited: boolean;

  /**
   * How hosts have rated this, sent to the owner alone.
   *
   * Null when nobody has reviewed it - and absent entirely for anybody but the
   * owner, because sending a review notifies the artist rather than publishing
   * to their audience. See `ReceivedReviewsAction`.
   */
  reviews?: { count: number; average: number | null } | null;
  reposted?: boolean;
  repost_count?: number;
  created_at: string;
  status: 'pending' | 'processing' | 'ready' | 'failed';
  failure_reason: string | null;
  visibility: 'public' | 'unlisted' | 'private';
  /** Declared by the artist, never guessed. See the migration. */
  explicit: boolean;
  /** Declared too, and for the same reason. See `TempoAndKey`. */
  bpm: number | null;
  key_signature: string | null;
  /**
   * Also always returned by `TrackRepository::shape()` and also never declared
   * here - the same gap as `description` above. It matters now because the
   * editor opens from this list: a genre TypeScript cannot see is a genre the
   * form starts empty and saves away.
   */
  genre_id: number | null;
  play_count: number;
  like_count: number;
}

/**
 * Somebody's tracks, newest first.
 *
 * ## Why unfinished tracks are shown at all
 *
 * To a visitor this is a list of playable tracks. To the owner it also includes
 * the ones still processing and the ones that failed - because otherwise the
 * upload flow ends with the track apparently vanishing for two minutes, and the
 * only honest reading of that is "it did not work".
 *
 * The server decides who sees which; this component renders what it is given
 * and never filters. A client-side filter would be a second, weaker copy of a
 * visibility rule that already exists (§6.7).
 *
 * ## The singles, and where the rest went
 *
 * This is the tracks that are not on one of the artist's own releases. The
 * albums are listed immediately above it, so a track on one is already on
 * this page inside the release it belongs to - see `ListTracksAction`.
 *
 * The artist gets the same list as everybody else, which means the row they
 * edit a released track from is on the release now. So when they have one,
 * this says so: an artist who uploaded sixty tracks and can see twelve needs
 * to be told where the others are, not left to conclude they went missing.
 *
 * ## Arranging it
 *
 * Newest first is the right default and a poor final answer - the track
 * somebody wants at the top of their page is the one they are pushing, not
 * the one they happened to upload last. So the owner can drag a row, and can
 * move it with two buttons, which is the same pair `PlaylistTracks` offers
 * and for the reason the album editor wrote down first: dragging needs a
 * pointer, and a keyboard or a phone gets nothing from it.
 *
 * The new order is shown immediately and sent as the whole list. If the send
 * fails the local order is dropped rather than left standing, because a
 * profile that looks rearranged and is not is worse than one that never
 * moved.
 */
export function TrackList({ username, isOwner }: { username: string; isOwner: boolean }) {
  const queryClient = useQueryClient();

  /*
   * Which row is open in the editor, by id rather than by object.
   *
   * The list refetches every three seconds while anything is processing, so a
   * held object would be a stale copy of a row that has since changed
   * underneath it - and the editor would save the version from before.
   */
  const [editing, setEditing] = useState<number | null>(null);

  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  const { items, isPending, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useArtistTracks(
    username,
    { singles: true },
  );

  /*
   * Whether they have a release at all, for the line that says where the
   * rest of their tracks are. The same query key the album grid on this
   * page already uses, so on a profile this costs nothing - and on the rare
   * surface that renders this list without one, one small request.
   */
  const { data: releases } = useQuery({
    queryKey: ['albums', username],
    queryFn: () => albums.list(username),
    enabled: isOwner,
  });

  const hasReleases = (releases?.items ?? []).some((collection) => collection.type !== 'playlist');

  /*
   * The order this session has put them in, until the server agrees.
   *
   * Held as ids rather than rows: the list refetches every three seconds
   * while anything is processing, so holding rows would pin stale copies of
   * them behind whatever the poll brought back.
   */
  const [arranged, setArranged] = useState<number[] | null>(null);
  const [dragging, setDragging] = useState<number | null>(null);

  const shown = useMemo(() => {
    if (arranged === null) return items;

    const byId = new Map(items.map((track) => [track.id, track]));

    return [
      ...arranged.map((id) => byId.get(id)).filter((track) => track !== undefined),
      // Anything that arrived after the drag - a finished upload, most
      // likely - rather than dropping it because it is not in the list.
      ...items.filter((track) => !arranged.includes(track.id)),
    ];
  }, [items, arranged]);

  const arrange = useMutation({
    mutationFn: (ids: number[]) => api.put('/me/tracks/order', { track_ids: ids }),
    onSuccess: async () => {
      /*
       * Wait for the list to come back before letting go of the local order,
       * so the rows do not jump back to where they were for the length of one
       * request and then return.
       */
      await queryClient.invalidateQueries({ queryKey: ['tracks'] });
      setArranged(null);
    },
    // Dropped, not kept: see the note on this component.
    onError: () => setArranged(null),
  });

  /** Move one row to another index, then send the whole order. */
  const move = (from: number, to: number) => {
    if (to < 0 || to >= shown.length || from === to) return;

    const next = [...shown];
    const [row] = next.splice(from, 1);

    if (row === undefined) return;

    next.splice(to, 0, row);

    const ids = next.map((track) => track.id);

    setArranged(ids);
    arrange.mutate(ids);
  };

  /*
   * Resolved from the live list on every render rather than held in state.
   *
   * The list refetches every three seconds while anything is processing, so a
   * captured object would be a copy of the row as it was when the editor
   * opened - and saving would write those values back over whatever changed.
   */
  const open = items.find((track) => track.id === editing);

  if (isPending) {
    return (
      // Skeletons, not a spinner: this list sits above the rest of a profile,
      // so a short spinner box that becomes a tall list shoves everything below
      // it down at the exact moment somebody starts reading.
      <div className="mt-4 flex flex-col gap-2" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <SkeletonRow key={i} />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="mt-4 p-6">
        <p className="text-sm text-soft">
          {isOwner && hasReleases ? (
            /*
              Not "nothing here yet": everything they have is on a release,
              and the release is where they can play and edit it.
            */
            <>Everything you have uploaded is on one of your releases. Open one to edit its tracks.</>
          ) : isOwner ? (
            <>
              Nothing here yet.{' '}
              <Link to="/upload" className="text-accent hover:underline">
                {copy['cta.upload']}
              </Link>{' '}
              to get started.
            </>
          ) : (
            /*
              A visitor is shown the singles, so an artist whose every
              track is on a release has an empty list here - and "nothing
              posted" would be a lie told about a full page of albums.
            */
            `Nothing from @${username} outside their releases.`
          )}
        </p>
      </Card>
    );
  }

  // The playable ones, in order, so pressing play on any of them queues the
  // rest of the profile behind it rather than stopping after one.
  const queue = shown.filter((track) => track.status === 'ready');

  return (
    <ul className="mt-4 flex flex-col gap-2" data-track-list>
      {shown.map((track, index) => {
        const isCurrent = current?.id === track.id;
        const ready = track.status === 'ready';

        return (
          // The identifier goes on the `li`, not the Card: Card takes only the
          // props it declares, so anything else is silently dropped.
          <li
            key={track.id}
            data-track-id={track.id}
            /*
              The whole row drags, the way a playlist's does. The handle says
              where to grab it; making only the handle draggable would mean a
              row that does not move when you pull it from the title, which is
              where people pull.

              ## This list sits inside something else that drags

              The owner's profile is made of sections they can rearrange, and
              `ProfileLayout` makes each section draggable as a whole. A row
              dragged inside one of them would otherwise be two drags at once:
              the event bubbles, so picking up a track told the section it was
              being picked up too, and dropping it moved the section.

              So a row's drag stops at the row. The events are only claimed
              while a *row* is what is moving - when the section is the thing
              being dragged, `dragging` is null and everything passes through,
              because the rows are part of the section's own drop target and
              must not become a hole in it.
            */
            draggable={isOwner}
            // Named the same as a profile section's, because a suite that
            // drags one has to be able to wait for the other.
            data-dragging={dragging === index ? 'true' : undefined}
            onDragStart={
              isOwner
                ? (event) => {
                    event.stopPropagation();
                    setDragging(index);
                  }
                : undefined
            }
            onDragOver={
              isOwner
                ? (event) => {
                    if (dragging === null) return;

                    event.preventDefault();
                    event.stopPropagation();
                  }
                : undefined
            }
            onDrop={
              isOwner
                ? (event) => {
                    if (dragging === null) return;

                    event.preventDefault();
                    event.stopPropagation();

                    move(dragging, index);
                    setDragging(null);
                  }
                : undefined
            }
            onDragEnd={
              isOwner
                ? (event) => {
                    event.stopPropagation();
                    setDragging(null);
                  }
                : undefined
            }
            className={dragging === index ? 'opacity-40' : ''}
          >
            <Card className="flex items-center gap-4 p-4">
              {isOwner && (
                <span className="flex shrink-0 flex-col items-center gap-0.5">
                  <Nudge
                    label={`Move ${track.title} up`}
                    attr={`up-${track.id}`}
                    disabled={index === 0}
                    onClick={() => move(index, index - 1)}
                  >
                    <path d="M8 4l4 5H4z" />
                  </Nudge>

                  <span
                    aria-hidden
                    title="Drag to reorder"
                    className="cursor-grab select-none font-mono text-[11px] leading-none text-ghost"
                  >
                    ⠿
                  </span>

                  <Nudge
                    label={`Move ${track.title} down`}
                    attr={`down-${track.id}`}
                    disabled={index === shown.length - 1}
                    onClick={() => move(index, index + 1)}
                  >
                    <path d="M8 12L4 7h8z" />
                  </Nudge>
                </span>
              )}

              <TrackThumb track={track} />

              <button
                type="button"
                disabled={!ready}
                aria-label={isCurrent && playing ? `Pause ${track.title}` : `Play ${track.title}`}
                onClick={() => (isCurrent ? toggle() : void play(track, queue, 'profile'))}
                className={[
                  'flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
                  'transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  ready ? 'bg-accent text-accent-ink hover:bg-accent-hover' : 'bg-raised text-faint',
                ].join(' ')}
              >
                {isCurrent && playing ? <PauseGlyph /> : <PlayGlyph />}
              </button>

              <div className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  {track.explicit && <Explicit />}
                  <DrawerLink
                    to={`/tracks/${track.id}`}
                    className="min-w-0 truncate text-sm font-medium text-ink hover:underline"
                  >
                    {track.title}
                  </DrawerLink>
                </span>

                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <span className="meta-caps">{duration(track.duration_ms)}</span>

                  {ready && (
                    <span className="meta-caps">
                      {track.play_count.toLocaleString()}{' '}
                      {track.play_count === 1 ? 'play' : 'plays'}
                    </span>
                  )}

                  {/*
                    Only the owner is ever sent a visibility other than public,
                    so this badge appears for nobody else by construction.
                  */}
                  {track.visibility !== 'public' && (
                    <Chip>{track.visibility === 'private' ? 'Private' : 'Unlisted'}</Chip>
                  )}

                  {track.status === 'processing' && <Chip tone="signal">Processing</Chip>}
                  {track.status === 'pending' && <Chip>Waiting for upload</Chip>}
                  {track.status === 'failed' && <Chip tone="hot">Failed</Chip>}
                </div>

                {/*
                  The reason, to the owner only. "Failed" with no explanation is
                  the most frustrating possible outcome for somebody who just
                  waited through an upload.
                */}
                {track.status === 'failed' && isOwner && track.failure_reason && (
                  <p className="mt-1.5 text-xs text-faint">{track.failure_reason}</p>
                )}
              </div>

              {/*
                What hosts said, for the owner. Draws nothing when nobody has
                reviewed it, which is most tracks - see `ReceivedReviews`.
              */}
              {isOwner && ready && (
                <ReceivedReviews
                  trackId={track.id}
                  title={track.title}
                  summary={track.reviews ?? null}
                />
              )}

              {/* Only on tracks that exist to be heard. A heart on something
                  still processing is an action with nothing behind it. */}
              {ready && (
                <FavoriteButton
                  trackId={track.id}
                  favorited={track.favorited}
                  likeCount={track.like_count}
                  showCount
                  size="sm"
                />
              )}

              {/*
                Beside the heart, and gated the same way: keeping a track and
                filing one are the same impulse, and neither means anything for
                something that is still transcoding.
              */}
              {ready && <AddToPlaylist trackId={track.id} title={track.title} size="sm" />}

              {/*
                The one thing an owner could not do to their own upload.
                Deliberately last and deliberately quiet - it is destructive,
                and a delete competing for attention with a play button is a
                delete somebody presses by accident.
              */}
              {/*
                Editing from here, rather than a trip to the track page.

                Everything the form touches - title, description, genre,
                visibility, explicit - is metadata the row already carries, so
                opening it costs no request. Not gated on `ready`: none of those
                fields depend on the audio existing, and the endpoint does not
                gate on it either.
              */}
              {isOwner && (
                <button
                  type="button"
                  data-edit-track={track.id}
                  onClick={() => setEditing(track.id)}
                  className="meta-caps shrink-0 text-faint transition-colors hover:text-ink"
                >
                  Edit
                </button>
              )}

              {isOwner && <DeleteTrack track={track} />}
            </Card>
          </li>
        );
      })}

      {/*
        One editor for the list, not one per row.

        Thirty rows would otherwise each hold a closed modal and its genre
        query, and the row that is open is the only one that needs either.
      */}
      {open !== undefined && (
        <TrackEditor
          track={open}
          cover={{
            artwork: open.artwork,
            /*
             * The picker polls this until the cover changes, and it wants one
             * track rather than the list - so the list is refetched and the row
             * picked back out of it. Returning a stale `open` would poll a
             * value that can never change and time out every time.
             */
            refetch: async () => {
              const fresh = await refetch();
              const row = fresh.data?.pages
                .flatMap((page) => page.items)
                .find((entry) => entry.id === open.id);

              return { artwork: row?.artwork ?? null };
            },
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refetch();
          }}
        />
      )}
      {arrange.isError && (
        <li className="mt-2">
          <Alert tone="error">That order did not save. The list is back as it was.</Alert>
        </li>
      )}

      {/*
        Said once, under the list, rather than as a tooltip on each handle.
        The second sentence only when there is a second page: what is not
        loaded cannot be dragged, and an artist who arranges fifty and
        wonders where the rest went has been told why.
      */}
      {isOwner && shown.length > 1 && (
        <li className="mt-1" data-tracks-arrange-hint>
          <p className="text-xs text-faint">
            Drag a track, or use the arrows, to set the order people see.
            {hasNextPage ? ' Show the rest first to arrange those too.' : ''}
          </p>
        </li>
      )}

      {/*
        Only to the artist, and only when there is a release for tracks to
        be on. To everybody else this list needs no explanation: it is the
        singles, above the albums, which is how a profile reads.
      */}
      {isOwner && hasReleases && (
        <li className="mt-1" data-tracks-on-releases>
          <p className="text-xs text-faint">
            Tracks on one of your releases are listed on the release, where you can also edit them.
          </p>
        </li>
      )}

      {hasNextPage && (
        <li className="mt-2 flex justify-center">
          <Button
            variant="ghost"
            loading={isFetchingNextPage}
            data-tracks-more
            onClick={() => void fetchNextPage()}
          >
            Show more
          </Button>
        </li>
      )}
    </ul>
  );
}

/**
 * The cover, at 200px, or a placeholder.
 *
 * A placeholder rather than nothing: without it rows with and without art have
 * different heights and the list stops scanning as a column.
 *
 * Exported because the favourites page draws the same row and had no cover at
 * all - and two copies of "a thumbnail, or the striped stand-in, at this exact
 * size and radius" is how one of them ends up looking slightly wrong.
 */
export function TrackThumb({ track }: { track: TrackSummary }) {
  const src = track.artwork_small ?? track.artwork;

  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] object-cover"
      />
    );
  }

  return (
    <div
      aria-hidden
      className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
      style={{ backgroundImage: 'repeating-linear-gradient(45deg, var(--color-blank) 0 6px, var(--color-blank-alt) 6px 12px)' }}
    />
  );
}

/** `4:07`, or an em dash while the duration is still unknown. */
function duration(ms: number | null): string {
  if (ms === null) return '—';

  const total = Math.round(ms / 1000);

  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

function PlayGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4 translate-x-px" fill="currentColor" aria-hidden="true">
      <path d="M4.5 2.8v10.4a.6.6 0 0 0 .93.5l8-5.2a.6.6 0 0 0 0-1l-8-5.2a.6.6 0 0 0-.93.5Z" />
    </svg>
  );
}

function PauseGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <rect x="4" y="2.5" width="3" height="11" rx="1" />
      <rect x="9" y="2.5" width="3" height="11" rx="1" />
    </svg>
  );
}

/**
 * One step up or down the list.
 *
 * The keyboard and touch path for arranging, next to the drag handle rather
 * than instead of it - `PlaylistTracks` carries the same pair, and the album
 * editor's comment explains why both exist.
 */
function Nudge({
  label,
  attr,
  disabled,
  onClick,
  children,
}: {
  label: string;
  attr: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      data-track-move={attr}
      disabled={disabled}
      onClick={onClick}
      className="flex h-4 w-5 items-center justify-center rounded-[var(--radius-chip)] text-ghost transition-colors hover:bg-raised hover:text-ink disabled:opacity-25"
    >
      <svg viewBox="0 0 16 16" className="h-2.5 w-2.5" fill="currentColor" aria-hidden="true">
        {children}
      </svg>
    </button>
  );
}

/**
 * Deleting your own track.
 *
 * ## Two presses, no dialog
 *
 * The button becomes its own confirmation. A modal would be the conventional
 * answer and is worse here: this sits inside a list row, and a dialog that
 * covers the list makes you confirm the deletion of something you can no longer
 * see. Arming in place keeps the title next to the button that removes it.
 *
 * It disarms on a timer, so an armed button somebody walked away from does not
 * stay armed for the next person to click.
 *
 * ## What it says
 *
 * "Delete for good", not "Delete". The audio, the original, the artwork and the
 * waveform all go, and so do the plays and comments - `DeleteTrackAction` spells
 * out what survives and why. None of it comes back, and the button should not
 * imply otherwise.
 */
function DeleteTrack({ track }: { track: TrackSummary }) {
  const [armed, setArmed] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), 4000);

    return () => clearTimeout(timer);
  }, [armed]);

  const remove = useMutation({
    mutationFn: () => api.delete(`/tracks/${track.id}`),
    onSuccess: async () => {
      // The profile's counts move too, so the whole profile is refetched
      // rather than the list alone.
      await queryClient.invalidateQueries({ queryKey: ['tracks'] });
      await queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return (
    <button
      type="button"
      data-delete-track={track.id}
      disabled={remove.isPending}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        if (armed) remove.mutate();
        else setArmed(true);
      }}
      className={`shrink-0 rounded-[var(--radius-chip)] border px-2.5 py-1.5 font-mono text-[10px] transition-colors ${
        armed
          ? 'border-hot-edge bg-hot-wash text-hot-text'
          : 'border-transparent text-ghost hover:border-line hover:text-soft'
      }`}
    >
      {remove.isPending ? 'deleting…' : armed ? 'delete for good?' : 'delete'}
    </button>
  );
}
